import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import axios from 'axios';
import AccessLogTab from '../components/admin/AccessLogTab';
import PendingDeletions from '../components/admin/PendingDeletions';
import BulkDeleteBar from '../components/admin/BulkDeleteBar';
import InactiveFilter from '../components/admin/InactiveFilter';
import CleanupReportCard from '../components/admin/CleanupReportCard';
import TestingPassesCard from '../components/admin/TestingPassesCard';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Users,
  Flag,
  ToggleLeft,
  ToggleRight,
  TrendingUp,
  TrendingDown,
  Activity,
  FileText,
  Receipt,
  MessageCircle,
  Shield,
  Search,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Calendar,
  RefreshCw,
  Crown,
  Clock,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Eye,
  PieChart,
  Layers,
  Plus,
  Trash2,
  CheckCircle,
  XCircle,
  Key,
  Copy,
  ThumbsUp,
  ThumbsDown,
  BookOpen,
  AlertTriangle,
  Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Trend indicator component - moved outside to avoid recreating on every render
const TrendIndicator = ({ trend, value, showPercent = true }) => {
  if (!trend) return <span className="text-gray-400">-</span>;
  
  const Icon = trend === 'up' ? ArrowUpRight : trend === 'down' ? ArrowDownRight : Minus;
  const colorClass = trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-500' : 'text-gray-400';
  
  return (
    <span className={`flex items-center gap-1 ${colorClass}`}>
      <Icon className="w-4 h-4" />
      {showPercent && value !== undefined && `${value > 0 ? '+' : ''}${value}%`}
    </span>
  );
};

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();

  const logPageView = (page) => {
    axios.post(`${API}/admin/access-log/page-view`, { page }, { withCredentials: true }).catch(() => {});
  };
  useEffect(() => { if (user?.is_admin) logPageView('admin:users'); }, [user?.is_admin]); // eslint-disable-line react-hooks/exhaustive-deps
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [confirmRemoveAdmin, setConfirmRemoveAdmin] = useState(null);
  const [confirmDeleteUserId, setConfirmDeleteUserId] = useState(null);
  const deleteConfirmTimer = useRef(null);
  const [deletionsRefresh, setDeletionsRefresh] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState({});
  const [inactiveMonths, setInactiveMonths] = useState('all');
  const inactiveRef = useRef('all');
  const toggleUser = (u) => setSelectedUsers((prev) => {
    const next = { ...prev };
    if (next[u.user_id]) delete next[u.user_id]; else next[u.user_id] = { user_id: u.user_id, email: u.email, paid: u.has_active_paid_subscription };
    return next;
  });
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [usageAnalytics, setUsageAnalytics] = useState(null);
  const [toolAnalytics, setToolAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  
  // New state for enhanced metrics
  const [returnAnalytics, setReturnAnalytics] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [perUserTools, setPerUserTools] = useState(null);
  const [perUserPagination, setPerUserPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [perUserSort, setPerUserSort] = useState({ by: 'total_activity', order: 'desc' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [userToolDetail, setUserToolDetail] = useState(null);
  
  // Admin accounts management state
  const [adminAccounts, setAdminAccounts] = useState([]);
  const [adminAccountsLoading, setAdminAccountsLoading] = useState(false);
  const [ragFeedback, setRagFeedback] = useState(null);
  const [ragFeedbackLoading, setRagFeedbackLoading] = useState(false);
  const [ragStatus, setRagStatus] = useState(null);
  const [customChunks, setCustomChunks] = useState([]);
  const [deletingChunk, setDeletingChunk] = useState(null);
  const [testQuery, setTestQuery] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [testingRetrieval, setTestingRetrieval] = useState(false);
  const testRetrievalRef = useRef(null);
  const [revenueStats, setRevenueStats] = useState(null);
  const [revenueWindow, setRevenueWindow] = useState('28');
  const [revenueLoading, setRevenueLoading] = useState(false);

  // Feature Flags state
  const [flags, setFlags]               = useState([]);
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [flagSearch, setFlagSearch]     = useState('');
  const [flagsError, setFlagsError]     = useState(null);
  const [flagAuditLog, setFlagAuditLog] = useState([]);
  const [showAudit, setShowAudit]       = useState(false);
  const [ragChunkModal, setRagChunkModal] = useState(null); // { user_message } | null
  const [ragChunkForm, setRagChunkForm] = useState({ pub_short: '', publication: '', section: '', text: '', url: '' });
  const [savingChunk, setSavingChunk] = useState(false);
  const [showAddAdminDialog, setShowAddAdminDialog] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminNotes, setNewAdminNotes] = useState('');
  const [addingAdmin, setAddingAdmin] = useState(false);
  
  // Password reset state
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLink, setResetLink] = useState('');
  const [generatingReset, setGeneratingReset] = useState(false);

  useEffect(() => {
    fetchOverview();
    fetchUsers();
    fetchUsageAnalytics();
    fetchToolAnalytics();
    fetchReturnAnalytics();
    fetchTrends();
    fetchPerUserTools();
    fetchAdminAccounts();
  }, []);

  const fetchOverview = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats/overview`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setOverview(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('toast.admin_access_required'));
        navigate('/dashboard');
      } else {
        toast.error(t('toast.failed_to_load_admin_stats'));
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Feature Flags ──────────────────────────────────────────────────────────

  const fetchFlags = async () => {
    setFlagsLoading(true);
    setFlagsError(null);
    try {
      const r = await axios.get(`${API}/feature-flags/admin/list`, { withCredentials: true });
      const data = r.data;
      setFlags(Array.isArray(data) ? data : (data.flags || []));
    } catch (e) {
      setFlagsError('Failed to load feature flags');
    } finally {
      setFlagsLoading(false);
    }
  };

  const fetchFlagAuditLog = async (flagName = null) => {
    try {
      const params = flagName ? `?flag_name=${flagName}` : '';
      const r = await axios.get(`${API}/feature-flags/admin/audit-log${params}`, { withCredentials: true });
      setFlagAuditLog(r.data?.events || []);
    } catch { /* silently ignore */ }
  };

  const setFlagEnabled = async (flagName, enabled) => {
    const endpoint = enabled
      ? `${API}/feature-flags/admin/${flagName}/enable`
      : `${API}/feature-flags/admin/${flagName}/disable`;
    try {
      await axios.post(endpoint, {}, { withCredentials: true });
      setFlags(prev => prev.map(f =>
        f.name === flagName ? { ...f, enabled } : f
      ));
    } catch {
      toast.error(`Failed to ${enabled ? 'enable' : 'disable'} flag`);
    }
  };

  const setFlagRollout = async (flagName, pct) => {
    try {
      await axios.post(
        `${API}/feature-flags/admin/${flagName}/rollout/${pct}`,
        {},
        { withCredentials: true }
      );
      setFlags(prev => prev.map(f =>
        f.name === flagName ? { ...f, rollout_percentage: pct } : f
      ));
    } catch {
      toast.error('Failed to update rollout percentage');
    }
  };

  const setFlagForUser = async (flagName, userId, enable) => {
    const endpoint = enable
      ? `${API}/feature-flags/admin/${flagName}/user/${userId}`
      : `${API}/feature-flags/admin/${flagName}/user/${userId}`;
    try {
      if (enable) {
        await axios.post(endpoint, {}, { withCredentials: true });
      } else {
        await axios.delete(endpoint, { withCredentials: true });
      }
    } catch {
      toast.error('Failed to update user flag');
    }
  };

  const fetchUsers = async (page = 1, search = '') => {    setUsersLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        sort_by: 'created_at',
        sort_order: 'desc'
      });
      if (search) params.append('search', search);
      if (inactiveRef.current !== 'all') params.append('inactive_months', inactiveRef.current);

      const response = await axios.get(`${API}/admin/users?${params}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      // Users list unavailable
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchUsageAnalytics = async (days = 30) => {
    try {
      const response = await axios.get(`${API}/admin/analytics/usage?days=${days}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setUsageAnalytics(response.data);
    } catch (error) {
      // Usage analytics unavailable
    }
  };

  const fetchToolAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics/tools`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setToolAnalytics(response.data);
    } catch (error) {
      // Tool analytics unavailable
    }
  };

  const fetchReturnAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics/returns`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setReturnAnalytics(response.data);
    } catch (error) {
      // Return analytics unavailable
    }
  };

  const fetchTrends = async (weeks = 4) => {
    try {
      const response = await axios.get(`${API}/admin/analytics/trends?weeks=${weeks}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setTrendsData(response.data);
    } catch (error) {
      // Trends unavailable
    }
  };

  const fetchPerUserTools = async (page = 1, sortBy = 'total_activity', sortOrder = 'desc') => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        sort_by: sortBy,
        sort_order: sortOrder
      });
      const response = await axios.get(`${API}/admin/analytics/per-user-tools?${params}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setPerUserTools(response.data);
      setPerUserPagination(response.data.pagination);
    } catch (error) {
      // Per-user tools unavailable
    }
  };

  const fetchUserToolDetail = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/user/${userId}/tools`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setUserToolDetail(response.data);
      setUserDetailOpen(true);
    } catch (error) {
      toast.error(t('toast.failed_to_load_user_details'));
    }
  };

  // Admin accounts management functions
  const fetchRevenueStats = async () => {
    setRevenueLoading(true);
    try {
      const res = await axios.get(`${API}/admin/revenue-stats`, {
        headers: getAuthHeaders(), withCredentials: true,
      });
      setRevenueStats(res.data);
    } catch {
      toast.error('Failed to load revenue stats');
    } finally {
      setRevenueLoading(false);
    }
  };

  const fetchRagFeedback = async () => {    setRagFeedbackLoading(true);
    try {
      const [feedbackRes, statusRes, chunksRes] = await Promise.all([
        axios.get(`${API}/admin/rag-feedback-stats`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/rag-status`,         { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/rag-chunks`,          { headers: getAuthHeaders(), withCredentials: true }),
      ]);
      setRagFeedback(feedbackRes.data);
      setRagStatus(statusRes.data);
      setCustomChunks(chunksRes.data.chunks || []);
    } catch (error) {
      toast.error('Failed to load RAG feedback stats');
    } finally {
      setRagFeedbackLoading(false);
    }
  };

  const deleteCustomChunk = async (chunkId, section) => {
    if (!window.confirm(`Remove "${section}" from the knowledge base?`)) return;
    setDeletingChunk(chunkId);
    try {
      await axios.delete(`${API}/admin/rag-chunks/${chunkId}`, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Chunk removed from knowledge base.');
      fetchRagFeedback();
    } catch (error) {
      toast.error('Failed to delete chunk');
    } finally {
      setDeletingChunk(null);
    }
  };

  const testRetrieval = async () => {
    if (!testQuery.trim()) return;
    setTestingRetrieval(true);
    setTestResults(null);
    try {
      const res = await axios.post(`${API}/admin/rag-test-retrieval`,
        { question: testQuery.trim(), top_k: 4 },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setTestResults(res.data);
    } catch (error) {
      toast.error('Retrieval test failed');
    } finally {
      setTestingRetrieval(false);
    }
  };

  const testThisQuery = async (message) => {
    setTestQuery(message);
    // Scroll to the Test Retrieval panel first
    setTimeout(() => {
      testRetrievalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    // Auto-fire the retrieval with this message
    setTestingRetrieval(true);
    setTestResults(null);
    try {
      const res = await axios.post(`${API}/admin/rag-test-retrieval`,
        { question: message.trim(), top_k: 4 },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setTestResults(res.data);
    } catch (error) {
      toast.error('Retrieval test failed');
    } finally {
      setTestingRetrieval(false);
    }
  };

  const openAddToRag = (item) => {
    // Pre-fill section from the user question — capitalise and trim question marks
    const rawSection = item.user_message.replace(/[?!]/g, '').trim();
    const section = rawSection.length > 60 ? rawSection.substring(0, 60) + '...' : rawSection;
    setRagChunkForm({ pub_short: '', publication: '', section, text: '', url: '' });
    setRagChunkModal(item);
  };

  const saveRagChunk = async () => {
    if (!ragChunkForm.pub_short.trim() || !ragChunkForm.section.trim() || !ragChunkForm.text.trim()) {
      toast.error('Publication short name, section, and content are required.');
      return;
    }
    setSavingChunk(true);
    try {
      await axios.post(`${API}/admin/rag-chunks`, ragChunkForm, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(`Added "${ragChunkForm.pub_short} — ${ragChunkForm.section}" to RAG knowledge base.`);
      setRagChunkModal(null);
      fetchRagFeedback(); // refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save chunk');
    } finally {
      setSavingChunk(false);
    }
  };

  const fetchAdminAccounts = async () => {
    setAdminAccountsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/accounts`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setAdminAccounts(response.data.admins || []);
    } catch (error) {
      // Admin accounts unavailable
    } finally {
      setAdminAccountsLoading(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!newAdminEmail.trim()) {
      toast.error(t('toast.please_enter_an_email_address'));
      return;
    }
    
    setAddingAdmin(true);
    try {
      await axios.post(`${API}/admin/accounts`, {
        email: newAdminEmail.trim(),
        name: newAdminName.trim() || null,
        notes: newAdminNotes.trim() || null
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      toast.success(t('toast.admin_created', { email: newAdminEmail }));
      setShowAddAdminDialog(false);
      setNewAdminEmail('');
      setNewAdminName('');
      setNewAdminNotes('');
      fetchAdminAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toast.failed_to_add_admin'));
    } finally {
      setAddingAdmin(false);
    }
  };

  const handleRemoveAdmin = async (email) => {
    if (confirmRemoveAdmin !== email) {
      setConfirmRemoveAdmin(email);
      toast.info('Click again to confirm removing admin', { duration: 3000 });
      setTimeout(() => setConfirmRemoveAdmin(null), 3000);
      return;
    }
    setConfirmRemoveAdmin(null);
    
    try {
      await axios.delete(`${API}/admin/accounts/${encodeURIComponent(email)}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(t('toast.admin_account_removed'));
      fetchAdminAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toast.failed_to_remove_admin'));
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    clearTimeout(deleteConfirmTimer.current);
    if (confirmDeleteUserId !== userId) {
      setConfirmDeleteUserId(userId);
      toast.info(`Click again to delete ${userEmail} (restorable for 14 days)`, { id: 'confirm-delete-user', duration: 4000 });
      deleteConfirmTimer.current = setTimeout(() => setConfirmDeleteUserId(null), 4000);
      return;
    }
    setConfirmDeleteUserId(null);
    toast.dismiss('confirm-delete-user');
    try {
      const response = await axios.delete(`${API}/admin/users/${userId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(response.data.message);
      fetchUsers(pagination.page, searchTerm);
      fetchOverview();
      setDeletionsRefresh((k) => k + 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleAdminStatus = async (email, currentStatus) => {
    try {
      await axios.put(`${API}/admin/accounts/${encodeURIComponent(email)}`, {
        is_active: !currentStatus
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(t('toast.admin_toggled', { action: currentStatus ? t('toast.deactivated') : t('toast.activated') }));
      fetchAdminAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toast.failed_to_update_admin'));
    }
  };

  // Password reset functions
  const handleGenerateResetLink = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      toast.error(t('toast.please_enter_an_email_address'));
      return;
    }
    
    setGeneratingReset(true);
    setResetLink('');
    
    try {
      const response = await axios.post(`${API}/admin/password-reset/generate`, {
        email: resetEmail.trim()
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      setResetLink(response.data.reset_link);
      toast.success(t('toast.reset_link_generated', { email: response.data.email }));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toast.failed_to_generate_reset_link'));
    } finally {
      setGeneratingReset(false);
    }
  };

  const copyResetLink = async () => {
    try {
      await navigator.clipboard.writeText(resetLink);
      toast.success(t('toast.reset_link_copied_to_clipboard'));
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = resetLink;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success(t('toast.reset_link_copied_to_clipboard'));
      } catch (fallbackError) {
        toast.error(t('toast.failed_to_copy_please_copy_manually'));
      }
      document.body.removeChild(textArea);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchUsers(newPage, searchTerm);
  };

  const handlePerUserPageChange = (newPage) => {
    fetchPerUserTools(newPage, perUserSort.by, perUserSort.order);
  };

  const handlePerUserSortChange = (sortBy) => {
    const newOrder = perUserSort.by === sortBy && perUserSort.order === 'desc' ? 'asc' : 'desc';
    setPerUserSort({ by: sortBy, order: newOrder });
    fetchPerUserTools(1, sortBy, newOrder);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateLong = (dateStr) => (dateStr
    ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Never');

  const formatDateShort = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatGrowth = (value) => {
    if (value > 0) return <span className="text-emerald-600 flex items-center gap-1"><TrendingUp className="w-4 h-4" />+{value}</span>;
    if (value < 0) return <span className="text-red-600 flex items-center gap-1"><TrendingDown className="w-4 h-4" />{value}</span>;
    return <span className="text-gray-500">0</span>;
  };

  const refreshAll = () => {
    fetchOverview();
    fetchUsers();
    fetchUsageAnalytics(analyticsDays);
    fetchToolAnalytics();
    fetchReturnAnalytics();
    fetchTrends();
    fetchPerUserTools();
    fetchAdminAccounts();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-emerald-600" />
              Admin Dashboard
            </h1>
            <p className="text-gray-600">Monitor users, growth, and platform analytics</p>
          </div>
          <Button onClick={refreshAll} variant="outline" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="total-users-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Users</p>
                  <p className="text-3xl font-bold text-gray-900">{overview?.users?.total || 0}</p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-4 text-sm">
                <span className="text-gray-500">Today: {overview?.users?.today || 0}</span>
                {formatGrowth(overview?.users?.growth?.day_over_day)}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="weekly-users-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">This Week</p>
                  <p className="text-3xl font-bold text-gray-900">{overview?.users?.this_week || 0}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-4 text-sm">
                <span className="text-gray-500">WoW:</span>
                {formatGrowth(overview?.users?.growth?.week_over_week)}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="monthly-users-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">This Month</p>
                  <p className="text-3xl font-bold text-gray-900">{overview?.users?.this_month || 0}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-4 text-sm">
                <span className="text-gray-500">MoM:</span>
                {formatGrowth(overview?.users?.growth?.month_over_month)}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="subscriptions-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Subscriptions</p>
                  <p className="text-3xl font-bold text-gray-900">{overview?.subscriptions?.active || 0}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Crown className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-500">
                + {overview?.subscriptions?.grandfathered || 0} grandfathered
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-slate-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-600" />
                <div>
                  <p className="text-2xl font-bold">{overview?.content?.transactions || 0}</p>
                  <p className="text-xs text-gray-500">Transactions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <Receipt className="w-5 h-5 text-slate-600" />
                <div>
                  <p className="text-2xl font-bold">{overview?.content?.receipts || 0}</p>
                  <p className="text-xs text-gray-500">Receipts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-slate-600" />
                <div>
                  <p className="text-2xl font-bold">{overview?.content?.chat_messages || 0}</p>
                  <p className="text-xs text-gray-500">AI Chats</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <Activity className="w-5 h-5 text-slate-600" />
                <div>
                  <p className="text-2xl font-bold">{overview?.content?.documents || 0}</p>
                  <p className="text-xs text-gray-500">Documents</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-4" onValueChange={(v) => logPageView(`admin:${v}`)}>
          <TabsList className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>
            <TabsTrigger value="admin-accounts" data-testid="admin-accounts-tab">Admin Accounts</TabsTrigger>
            <TabsTrigger value="per-user-tools" data-testid="per-user-tools-tab">Per-User Tools</TabsTrigger>
            <TabsTrigger value="trends" data-testid="trends-tab">Trends</TabsTrigger>
            <TabsTrigger value="returns" data-testid="returns-tab">Return Analytics</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab">Usage Analytics</TabsTrigger>
            <TabsTrigger value="tools" data-testid="tools-tab">Tool Breakdown</TabsTrigger>
            <TabsTrigger value="ai-feedback" data-testid="ai-feedback-tab" onClick={fetchRagFeedback}>AI Feedback</TabsTrigger>
            <TabsTrigger value="revenue" data-testid="revenue-tab" onClick={fetchRevenueStats}>Revenue</TabsTrigger>
            <TabsTrigger value="flags" data-testid="flags-tab" onClick={fetchFlags}
              className="flex items-center gap-1.5">
              <Flag className="w-3.5 h-3.5" />
              Feature Flags
            </TabsTrigger>
            <TabsTrigger value="access-log" data-testid="access-log-tab" className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" />
              Access Log
            </TabsTrigger>
            <TabsTrigger value="testing-passes" data-testid="testing-passes-tab" className="flex items-center gap-1.5">
              <Key className="w-3.5 h-3.5" />
              Testing Passes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="testing-passes">
            <TestingPassesCard />
          </TabsContent>

          <TabsContent value="access-log">
            <CleanupReportCard />
            <AccessLogTab />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <PendingDeletions
              refreshKey={deletionsRefresh}
              onChanged={() => { fetchUsers(pagination.page, searchTerm); fetchOverview(); }}
            />
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <CardTitle>All Users</CardTitle>
                  <form onSubmit={handleSearch} className="flex gap-2">
                    <Input
                      placeholder="Search by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-64"
                      data-testid="user-search-input"
                    />
                    <Button type="submit" variant="outline" data-testid="user-search-btn">
                      <Search className="w-4 h-4" />
                    </Button>
                    <InactiveFilter
                      value={inactiveMonths}
                      onChange={(v) => { inactiveRef.current = v; setInactiveMonths(v); setSelectedUsers({}); fetchUsers(1, searchTerm); }}
                    />
                  </form>
                </div>
              </CardHeader>
              <CardContent>
                {usersLoading ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10">
                              <Checkbox
                                checked={users.length > 0 && users.every((u) => selectedUsers[u.user_id])}
                                onCheckedChange={(v) => setSelectedUsers((prev) => {
                                  const next = { ...prev };
                                  users.forEach((u) => { if (v) next[u.user_id] = { user_id: u.user_id, email: u.email, paid: u.has_active_paid_subscription }; else delete next[u.user_id]; });
                                  return next;
                                })}
                                data-testid="select-all-users-checkbox"
                              />
                            </TableHead>
                            <TableHead>User</TableHead>
                            <TableHead>Business Type</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Activity</TableHead>
                            <TableHead>Joined</TableHead>
                            <TableHead>Last Login</TableHead>
                            <TableHead>Last Activity</TableHead>
                            <TableHead className="w-12"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {users.map((u) => (
                            <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`} data-state={selectedUsers[u.user_id] ? 'selected' : undefined}>
                              <TableCell>
                                <Checkbox checked={!!selectedUsers[u.user_id]} onCheckedChange={() => toggleUser(u)}
                                  data-testid={`select-user-${u.user_id}`} />
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{u.name}</p>
                                  <p className="text-sm text-gray-500">{u.email}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="capitalize">
                                  {u.business_type?.replace(/_/g, ' ') || 'N/A'}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {u.subscription_status === 'grandfathered' && (
                                  <Badge className="bg-amber-100 text-amber-800">
                                    <Crown className="w-3 h-3 mr-1" />
                                    Grandfathered
                                  </Badge>
                                )}
                                {u.subscription_status === 'active' && (
                                  <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                                )}
                                {u.subscription_status === 'none' && (
                                  <Badge variant="secondary">No Sub</Badge>
                                )}
                                {inactiveMonths !== 'all' && u.has_active_paid_subscription && (
                                  <Badge className="ml-1 bg-red-100 text-red-700 hover:bg-red-100" title="Still paying — check before deleting"
                                    data-testid={`paid-subscriber-badge-${u.user_id}`}>Paid subscriber</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="text-sm">
                                  <span className="text-gray-900">{u.transaction_count}</span>
                                  <span className="text-gray-400"> txns</span>
                                  <span className="mx-1">·</span>
                                  <span className="text-gray-900">{u.receipt_count}</span>
                                  <span className="text-gray-400"> rcpts</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-sm text-gray-500">
                                {inactiveMonths !== 'all' ? formatDateLong(u.created_at) : formatDateShort(u.created_at)}
                              </TableCell>
                              <TableCell className="text-sm">
                                {u.last_login ? (
                                  <span className={`flex items-center gap-1 ${inactiveMonths !== 'all' ? 'text-amber-600' : 'text-emerald-600'}`}>
                                    <Clock className="w-3 h-3" />
                                    {inactiveMonths !== 'all' ? formatDateLong(u.last_login) : formatDateShort(u.last_login)}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">Never</span>
                                )}
                                {u.inactivity_warned_at && (
                                  <span className="block text-[11px] text-amber-700 mt-0.5" data-testid={`warned-on-${u.user_id}`}>
                                    Warned on {formatDateLong(u.inactivity_warned_at)}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-sm text-gray-500">
                                {u.last_activity ? formatDateShort(u.last_activity) : <span className="text-gray-400">None</span>}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteUser(u.user_id, u.email)}
                                  className={confirmDeleteUserId === u.user_id ? "bg-red-500 text-white hover:bg-red-600" : "text-gray-400 hover:text-red-600"}
                                  data-testid={`delete-user-${u.user_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <BulkDeleteBar
                      selected={selectedUsers}
                      onClear={() => setSelectedUsers({})}
                      onDone={() => { fetchUsers(pagination.page, searchTerm); fetchOverview(); setDeletionsRefresh((k) => k + 1); }}
                    />

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-gray-500">
                        Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagination.page === 1}
                          onClick={() => handlePageChange(pagination.page - 1)}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="px-3 py-1 text-sm">
                          Page {pagination.page} of {pagination.total_pages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagination.page >= pagination.total_pages}
                          onClick={() => handlePageChange(pagination.page + 1)}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Admin Accounts Tab */}
          <TabsContent value="admin-accounts">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Admin Accounts
                    </CardTitle>
                    <CardDescription>Manage who has administrative access to this dashboard</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowResetPasswordDialog(true)} data-testid="reset-password-btn">
                      <Key className="w-4 h-4 mr-2" />
                      Reset User Password
                    </Button>
                    <Button onClick={() => setShowAddAdminDialog(true)} data-testid="add-admin-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Admin
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {adminAccountsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-md border overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-3 font-medium">Email</th>
                            <th className="text-left p-3 font-medium">Name</th>
                            <th className="text-left p-3 font-medium">Type</th>
                            <th className="text-left p-3 font-medium">Status</th>
                            <th className="text-left p-3 font-medium">Notes</th>
                            <th className="text-left p-3 font-medium">Last Login</th>
                            <th className="text-right p-3 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminAccounts.map((admin, idx) => (
                            <tr key={admin.email} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                              <td className="p-3 font-medium">{admin.email}</td>
                              <td className="p-3 text-gray-600">{admin.name || '-'}</td>
                              <td className="p-3">
                                <Badge variant={admin.type === 'grandfathered' ? 'default' : 'secondary'}>
                                  {admin.type === 'grandfathered' ? 'Founding' : 'Added'}
                                </Badge>
                              </td>
                              <td className="p-3">
                                <Badge variant={admin.is_active ? 'success' : 'destructive'} className={admin.is_active ? 'bg-emerald-100 text-emerald-800' : ''}>
                                  {admin.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td className="p-3 text-gray-500 text-xs max-w-[200px] truncate" title={admin.notes}>
                                {admin.notes || '-'}
                              </td>
                              <td className="p-3 text-gray-500 text-xs">
                                {admin.last_login ? formatDate(admin.last_login) : 'Never'}
                              </td>
                              <td className="p-3 text-right">
                                {admin.type !== 'grandfathered' && (
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleToggleAdminStatus(admin.email, admin.is_active)}
                                      title={admin.is_active ? 'Deactivate' : 'Activate'}
                                    >
                                      {admin.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                                    </Button>
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => handleRemoveAdmin(admin.email)}
                                      title="Remove Admin"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                          {adminAccounts.length === 0 && (
                            <tr>
                              <td colSpan={7} className="p-8 text-center text-gray-500">
                                No admin accounts found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-gray-500">
                      Note: Founding admins (grandfathered) cannot be modified or removed. They are permanent admin accounts.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add Admin Dialog */}
            <Dialog open={showAddAdminDialog} onOpenChange={setShowAddAdminDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Admin Account</DialogTitle>
                  <DialogDescription>
                    Grant admin access to a user. They must have an existing account to log in.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddAdmin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin-email">Email Address *</Label>
                    <Input
                      id="admin-email"
                      type="email"
                      placeholder="user@example.com"
                      value={newAdminEmail}
                      onChange={(e) => setNewAdminEmail(e.target.value)}
                      required
                      data-testid="new-admin-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-name">Display Name (optional)</Label>
                    <Input
                      id="admin-name"
                      type="text"
                      placeholder="John Doe"
                      value={newAdminName}
                      onChange={(e) => setNewAdminName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-notes">Notes (optional)</Label>
                    <Input
                      id="admin-notes"
                      type="text"
                      placeholder="e.g., Support team lead"
                      value={newAdminNotes}
                      onChange={(e) => setNewAdminNotes(e.target.value)}
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowAddAdminDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={addingAdmin} data-testid="confirm-add-admin">
                      {addingAdmin ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                      Add Admin
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    Generate Password Reset Link
                  </DialogTitle>
                  <DialogDescription>
                    Generate a password reset link for any user. The link will be valid for 24 hours.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleGenerateResetLink} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">User Email Address</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="user@example.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      data-testid="reset-email-input"
                    />
                  </div>
                  
                  {resetLink && (
                    <div className="space-y-2">
                      <Label>Reset Link (click to copy)</Label>
                      <div 
                        className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg cursor-pointer hover:bg-emerald-100 transition-colors"
                        onClick={copyResetLink}
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-emerald-800 font-mono break-all pr-2">
                            {resetLink}
                          </p>
                          <Copy className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        Send this link to the user. It expires in 24 hours.
                      </p>
                    </div>
                  )}
                  
                  <DialogFooter>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setShowResetPasswordDialog(false);
                        setResetEmail('');
                        setResetLink('');
                      }}
                    >
                      Close
                    </Button>
                    <Button type="submit" disabled={generatingReset} data-testid="generate-reset-btn">
                      {generatingReset ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Key className="w-4 h-4 mr-2" />
                      )}
                      Generate Link
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Per-User Tools Tab */}
          <TabsContent value="per-user-tools">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle>Per-User Tool Usage</CardTitle>
                    <CardDescription>Detailed tool usage metrics for each user</CardDescription>
                  </div>
                  {perUserTools?.averages && (
                    <div className="flex gap-4 text-sm">
                      <div className="text-center px-3 py-1 bg-slate-100 rounded">
                        <p className="font-semibold">{perUserTools.averages.transactions_per_user}</p>
                        <p className="text-xs text-gray-500">Avg Txns</p>
                      </div>
                      <div className="text-center px-3 py-1 bg-slate-100 rounded">
                        <p className="font-semibold">{perUserTools.averages.receipts_per_user}</p>
                        <p className="text-xs text-gray-500">Avg Receipts</p>
                      </div>
                      <div className="text-center px-3 py-1 bg-slate-100 rounded">
                        <p className="font-semibold">{perUserTools.averages.chat_messages_per_user}</p>
                        <p className="text-xs text-gray-500">Avg Chats</p>
                      </div>
                      <div className="text-center px-3 py-1 bg-emerald-100 rounded">
                        <p className="font-semibold">{formatCurrency(perUserTools.averages.income_per_user)}</p>
                        <p className="text-xs text-gray-500">Avg Income</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => handlePerUserSortChange('last_login')}
                        >
                          Last Login {perUserSort.by === 'last_login' && (perUserSort.order === 'desc' ? '↓' : '↑')}
                        </TableHead>
                        <TableHead>Txns</TableHead>
                        <TableHead>CSV</TableHead>
                        <TableHead>Receipts</TableHead>
                        <TableHead>AI Chat</TableHead>
                        <TableHead>Audit</TableHead>
                        <TableHead>Tax Pkg</TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => handlePerUserSortChange('total_activity')}
                        >
                          Total {perUserSort.by === 'total_activity' && (perUserSort.order === 'desc' ? '↓' : '↑')}
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => handlePerUserSortChange('total_income')}
                        >
                          Income {perUserSort.by === 'total_income' && (perUserSort.order === 'desc' ? '↓' : '↑')}
                        </TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {perUserTools?.users?.map((u) => (
                        <TableRow key={u.user_id} data-testid={`per-user-row-${u.user_id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{u.name || 'Unnamed'}</p>
                              <p className="text-xs text-gray-500 truncate max-w-[150px]">{u.email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {u.last_login ? (
                              <span className="text-emerald-600">{formatDateShort(u.last_login)}</span>
                            ) : (
                              <span className="text-gray-400">Never</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{u.tools.transactions}</TableCell>
                          <TableCell className="text-sm">{u.tools.csv_imports}</TableCell>
                          <TableCell className="text-sm">{u.tools.receipts}</TableCell>
                          <TableCell className="text-sm">{u.tools.chat_messages}</TableCell>
                          <TableCell className="text-sm">{u.tools.audit_reports}</TableCell>
                          <TableCell className="text-sm">{u.tools.tax_packages}</TableCell>
                          <TableCell className="text-sm font-semibold">{u.total_activity}</TableCell>
                          <TableCell className="text-sm font-semibold text-emerald-600">
                            {formatCurrency(u.financials.total_income)}
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => fetchUserToolDetail(u.user_id)}
                              data-testid={`view-user-detail-${u.user_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-gray-500">
                    Showing {((perUserPagination.page - 1) * perUserPagination.limit) + 1} - {Math.min(perUserPagination.page * perUserPagination.limit, perUserPagination.total)} of {perUserPagination.total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={perUserPagination.page === 1}
                      onClick={() => handlePerUserPageChange(perUserPagination.page - 1)}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="px-3 py-1 text-sm">
                      Page {perUserPagination.page} of {perUserPagination.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={perUserPagination.page >= perUserPagination.total_pages}
                      onClick={() => handlePerUserPageChange(perUserPagination.page + 1)}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      Usage Trends & Growth
                    </CardTitle>
                    <CardDescription>Week-over-week tool usage growth and decline</CardDescription>
                  </div>
                  <Select defaultValue="4" onValueChange={(v) => fetchTrends(parseInt(v))}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4 weeks</SelectItem>
                      <SelectItem value="8">8 weeks</SelectItem>
                      <SelectItem value="12">12 weeks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {trendsData && (
                  <>
                    {/* Trend Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
                      {Object.entries(trendsData.trends || {}).slice(0, 5).map(([key, data]) => (
                        <Card key={key} className="bg-slate-50">
                          <CardContent className="pt-4 pb-4">
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                              <TrendIndicator trend={data.trend} value={data.percent_change} />
                            </div>
                            <p className="text-2xl font-bold mt-1">{data.current}</p>
                            <p className="text-xs text-gray-400">
                              vs {data.previous} last week ({data.change >= 0 ? '+' : ''}{data.change})
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Weekly Data Table */}
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Period</TableHead>
                            <TableHead className="text-right">New Users</TableHead>
                            <TableHead className="text-right">Transactions</TableHead>
                            <TableHead className="text-right">CSV Imports</TableHead>
                            <TableHead className="text-right">Receipts</TableHead>
                            <TableHead className="text-right">AI Chats</TableHead>
                            <TableHead className="text-right">Audit Reports</TableHead>
                            <TableHead className="text-right">Tax Packages</TableHead>
                            <TableHead className="text-right font-semibold">Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {trendsData.weekly_data?.map((week, idx) => (
                            <TableRow key={week.week} className={idx === 0 ? 'bg-emerald-50' : ''}>
                              <TableCell className="font-medium">
                                <div>
                                  <p>{week.week_label}</p>
                                  <p className="text-xs text-gray-400">{week.period}</p>
                                </div>
                              </TableCell>
                              <TableCell className="text-right">{week.new_users}</TableCell>
                              <TableCell className="text-right">{week.transactions}</TableCell>
                              <TableCell className="text-right">{week.csv_imports}</TableCell>
                              <TableCell className="text-right">{week.receipts}</TableCell>
                              <TableCell className="text-right">{week.chat_messages}</TableCell>
                              <TableCell className="text-right">{week.audit_reports}</TableCell>
                              <TableCell className="text-right">{week.tax_packages}</TableCell>
                              <TableCell className="text-right font-bold">{week.total_activity}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Return Analytics Tab */}
          <TabsContent value="returns">
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="bg-emerald-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-8 h-8 text-emerald-600" />
                      <div>
                        <p className="text-sm text-gray-500">Avg Income</p>
                        <p className="text-2xl font-bold text-emerald-700">
                          {formatCurrency(returnAnalytics?.summary?.avg_income)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-red-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Receipt className="w-8 h-8 text-red-600" />
                      <div>
                        <p className="text-sm text-gray-500">Avg Expenses</p>
                        <p className="text-2xl font-bold text-red-700">
                          {formatCurrency(returnAnalytics?.summary?.avg_expenses)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Layers className="w-8 h-8 text-blue-600" />
                      <div>
                        <p className="text-sm text-gray-500">Avg Net Income</p>
                        <p className="text-2xl font-bold text-blue-700">
                          {formatCurrency(returnAnalytics?.summary?.avg_net_income)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <PieChart className="w-8 h-8 text-purple-600" />
                      <div>
                        <p className="text-sm text-gray-500">Avg Est. Tax</p>
                        <p className="text-2xl font-bold text-purple-700">
                          {formatCurrency(returnAnalytics?.summary?.avg_estimated_tax)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Income Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Income Distribution</CardTitle>
                    <CardDescription>Users grouped by total income range</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {/* ── Commandment II: use income_distribution_with_pct from backend ── */}
                      {(returnAnalytics?.income_distribution_with_pct
                        ? Object.entries(returnAnalytics.income_distribution_with_pct)
                        : returnAnalytics?.income_distribution
                          ? Object.entries(returnAnalytics.income_distribution).map(([range, count]) => {
                              const total = Object.values(returnAnalytics.income_distribution).reduce((a, b) => a + b, 0);
                              return [range, { count, pct: total > 0 ? parseFloat((count / total * 100).toFixed(1)) : 0 }];
                            })
                          : []
                      ).map(([range, data]) => {
                        const count = typeof data === 'object' ? data.count : data;
                        const pct   = typeof data === 'object' ? data.pct   : 0;
                        const labels = {
                          'under_25k': 'Under $25K', '25k_50k': '$25K - $50K',
                          '50k_100k': '$50K - $100K', '100k_250k': '$100K - $250K',
                          'over_250k': 'Over $250K'
                        };
                        return (
                          <div key={range}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-600">{labels[range] || range}</span>
                              <span className="font-medium">{count} users ({pct}%)</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-emerald-500 rounded-full transition-all"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Top Earners */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Top Earners</CardTitle>
                    <CardDescription>Users with highest total income</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {returnAnalytics?.top_earners?.slice(0, 8).map((u, idx) => (
                        <div key={u.user_id} className="flex items-center justify-between py-2 border-b last:border-0">
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx < 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {idx + 1}
                            </span>
                            <div>
                              <p className="font-medium text-sm">{u.name || 'Unnamed'}</p>
                              <p className="text-xs text-gray-500 truncate max-w-[150px]">{u.email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-emerald-600">{formatCurrency(u.total_income)}</p>
                            <p className="text-xs text-gray-400">Net: {formatCurrency(u.net_income)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Totals Summary */}
              <Card className="bg-slate-50">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-gray-500">Users With Data</p>
                      <p className="text-3xl font-bold">{returnAnalytics?.summary?.users_with_data || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Income (All Users)</p>
                      <p className="text-3xl font-bold text-emerald-600">
                        {formatCurrency(returnAnalytics?.summary?.total_income_all_users)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Expenses (All Users)</p>
                      <p className="text-3xl font-bold text-red-600">
                        {formatCurrency(returnAnalytics?.summary?.total_expenses_all_users)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Usage Over Time</CardTitle>
                  <Select value={analyticsDays.toString()} onValueChange={(v) => { setAnalyticsDays(parseInt(v)); fetchUsageAnalytics(parseInt(v)); }}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {usageAnalytics && (
                  <>
                    {/* Totals Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-emerald-600">{usageAnalytics.totals.new_users}</p>
                        <p className="text-xs text-gray-500">New Users</p>
                      </div>
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">{usageAnalytics.totals.transactions}</p>
                        <p className="text-xs text-gray-500">Transactions</p>
                      </div>
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-purple-600">{usageAnalytics.totals.receipts}</p>
                        <p className="text-xs text-gray-500">Receipts</p>
                      </div>
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-amber-600">{usageAnalytics.totals.chat_messages}</p>
                        <p className="text-xs text-gray-500">AI Chats</p>
                      </div>
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-red-600">{usageAnalytics.totals.audit_reports}</p>
                        <p className="text-xs text-gray-500">Audit Reports</p>
                      </div>
                    </div>

                    {/* Daily Table */}
                    <div className="max-h-96 overflow-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">New Users</TableHead>
                            <TableHead className="text-right">Transactions</TableHead>
                            <TableHead className="text-right">Receipts</TableHead>
                            <TableHead className="text-right">AI Chats</TableHead>
                            <TableHead className="text-right">Total Activity</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {usageAnalytics.daily_stats.slice().reverse().map((day) => (
                            <TableRow key={day.date}>
                              <TableCell className="font-medium">{day.date}</TableCell>
                              <TableCell className="text-right">{day.new_users}</TableCell>
                              <TableCell className="text-right">{day.transactions}</TableCell>
                              <TableCell className="text-right">{day.receipts}</TableCell>
                              <TableCell className="text-right">{day.chat_messages}</TableCell>
                              <TableCell className="text-right font-semibold">{day.total_activity}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    <div className="mt-4 text-sm text-gray-500">
                      Average daily activity: <span className="font-semibold">{usageAnalytics.averages.daily_activity}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tools Tab */}
          <TabsContent value="tools">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Transactions Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Transaction Sources</CardTitle>
                </CardHeader>
                <CardContent>
                  {toolAnalytics && (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">CSV Imports</span>
                        <span className="font-semibold">{toolAnalytics.transactions.csv_imports}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Manual Entry</span>
                        <span className="font-semibold">{toolAnalytics.transactions.manual_entry}</span>
                      </div>
                      <div className="border-t pt-2 flex justify-between items-center font-semibold">
                        <span>Total</span>
                        <span>{toolAnalytics.transactions.total}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Receipts Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Receipt Processing</CardTitle>
                </CardHeader>
                <CardContent>
                  {toolAnalytics && (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">OCR Scanned</span>
                        <span className="font-semibold">{toolAnalytics.receipts.ocr_scanned}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Manual Upload</span>
                        <span className="font-semibold">{toolAnalytics.receipts.manual_upload}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Linked to Transactions</span>
                        <span className="font-semibold text-emerald-600">{toolAnalytics.receipts.linked_to_transactions}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Feature Usage */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Feature Usage</CardTitle>
                </CardHeader>
                <CardContent>
                  {toolAnalytics && (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Mileage Entries</span>
                        <span className="font-semibold">{toolAnalytics.features.mileage_entries}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Contractors</span>
                        <span className="font-semibold">{toolAnalytics.features.contractors}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Audit Reports</span>
                        <span className="font-semibold">{toolAnalytics.features.audit_reports}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Tax Packages</span>
                        <span className="font-semibold">{toolAnalytics.features.tax_packages}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Reminders</span>
                        <span className="font-semibold">{toolAnalytics.features.reminders}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* User Demographics */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">User Demographics</CardTitle>
                </CardHeader>
                <CardContent>
                  {toolAnalytics && (
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500 mb-2">Business Types</p>
                        <div className="space-y-2">
                          {Object.entries(toolAnalytics.user_demographics.business_types).map(([type, count]) => (
                            <div key={type} className="flex justify-between items-center">
                              <span className="text-gray-600 capitalize">{type.replace(/_/g, ' ')}</span>
                              <Badge variant="secondary">{count}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="border-t pt-4">
                        <p className="text-sm text-gray-500 mb-2">Filing Status</p>
                        <div className="space-y-2">
                          {Object.entries(toolAnalytics.user_demographics.filing_statuses).map(([status, count]) => (
                            <div key={status} className="flex justify-between items-center">
                              <span className="text-gray-600 capitalize">{status.replace(/_/g, ' ')}</span>
                              <Badge variant="secondary">{count}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* AI Feedback Tab */}
          <TabsContent value="ai-feedback" data-testid="ai-feedback-content">
            <div className="space-y-6">

              {ragFeedbackLoading && (
                <div className="flex items-center justify-center h-48">
                  <RefreshCw className="w-6 h-6 animate-spin text-emerald-500" />
                </div>
              )}

              {!ragFeedbackLoading && !ragFeedback && (
                <Card>
                  <CardContent className="p-12 text-center">
                    <BookOpen className="w-12 h-12 mx-auto text-stone-300 mb-3" />
                    <p className="text-stone-500 mb-4">Click the tab to load feedback data</p>
                    <Button onClick={fetchRagFeedback} variant="outline" data-testid="load-feedback-btn">
                      Load RAG Feedback Stats
                    </Button>
                  </CardContent>
                </Card>
              )}

              {!ragFeedbackLoading && ragFeedback && (
                <>
                  {/* RAG Status Bar */}
                  {ragStatus && (
                    <div className={`flex flex-wrap items-center gap-4 px-4 py-3 rounded-xl border text-sm ${
                      ragStatus.status === 'healthy'
                        ? 'bg-emerald-50 border-emerald-200'
                        : 'bg-amber-50 border-amber-200'
                    }`} data-testid="rag-status-bar">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${ragStatus.status === 'healthy' ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
                        <span className={`font-semibold capitalize ${ragStatus.status === 'healthy' ? 'text-emerald-700' : 'text-amber-700'}`}>
                          {ragStatus.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-stone-600">
                        <BookOpen className="w-3.5 h-3.5 text-stone-400" />
                        <span><strong data-testid="rag-total-chunks">{ragStatus.total_chunks}</strong> total chunks</span>
                      </div>
                      <div className="text-stone-400">|</div>
                      <div className="text-stone-600">
                        <strong>{ragStatus.base_chunks}</strong> IRS base
                      </div>
                      <div className="text-stone-400">+</div>
                      <div className="text-stone-600">
                        <strong data-testid="rag-custom-count">{ragStatus.custom_chunks}</strong> custom
                      </div>
                      <div className="text-stone-400">|</div>
                      <div className="text-stone-500 text-xs">
                        model: <code className="bg-stone-100 px-1 rounded">{ragStatus.model}</code>
                      </div>
                      <div className="text-stone-400 text-xs ml-auto">
                        checked {ragStatus.checked_at ? new Date(ragStatus.checked_at).toLocaleTimeString() : '—'}
                      </div>
                      <button
                        onClick={fetchRagFeedback}
                        className="ml-2 p-1 rounded hover:bg-white/60 text-stone-400 hover:text-emerald-600 transition-colors"
                        title="Refresh status"
                        data-testid="rag-status-refresh"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  {/* Summary stat cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-white border-stone-200">
                      <CardContent className="p-5">
                        <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Total Votes</p>
                        <p className="text-3xl font-bold text-stone-900" data-testid="total-feedback-count">
                          {ragFeedback.summary.total_feedback}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className={`border-2 ${ragFeedback.summary.satisfaction_rate >= 70 ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                      <CardContent className="p-5">
                        <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Satisfaction Rate</p>
                        <p className={`text-3xl font-bold ${ragFeedback.summary.satisfaction_rate >= 70 ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="satisfaction-rate">
                          {ragFeedback.summary.satisfaction_rate}%
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-emerald-50 border-emerald-200">
                      <CardContent className="p-5">
                        <div className="flex items-center gap-2 mb-1">
                          <ThumbsUp className="w-4 h-4 text-emerald-600" />
                          <p className="text-xs text-stone-500 uppercase tracking-wide">Helpful</p>
                        </div>
                        <p className="text-3xl font-bold text-emerald-700" data-testid="helpful-count">
                          {ragFeedback.summary.total_helpful}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-red-50 border-red-200">
                      <CardContent className="p-5">
                        <div className="flex items-center gap-2 mb-1">
                          <ThumbsDown className="w-4 h-4 text-red-500" />
                          <p className="text-xs text-stone-500 uppercase tracking-wide">Unhelpful</p>
                        </div>
                        <p className="text-3xl font-bold text-red-600" data-testid="unhelpful-count">
                          {ragFeedback.summary.total_unhelpful}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* RAG Improvement Backlog */}
                    <Card className="border-stone-200">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-stone-900">
                          <Zap className="w-5 h-5 text-amber-500" />
                          RAG Improvement Backlog
                        </CardTitle>
                        <CardDescription>
                          Topics ranked by unhelpful votes — prioritize expanding these IRS chunks
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {ragFeedback.improvement_topics.length === 0 ? (
                          <div className="text-center py-8">
                            <CheckCircle className="w-10 h-10 mx-auto text-emerald-400 mb-2" />
                            <p className="text-stone-500 text-sm">No improvement signals yet — keep collecting feedback!</p>
                          </div>
                        ) : (
                          <div className="space-y-3" data-testid="improvement-topics">
                            {ragFeedback.improvement_topics.map((topic, idx) => {
                              const barWidth = Math.max(4, Math.min(100,
                                ragFeedback.improvement_topics[0]?.unhelpful_count > 0
                                  ? (topic.unhelpful_count / ragFeedback.improvement_topics[0].unhelpful_count) * 100
                                  : 0
                              ));
                              const isHighPriority = topic.unhelpful_rate > 50;
                              return (
                                <div key={idx} className="space-y-1" data-testid={`topic-row-${idx}`}>
                                  <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                      {isHighPriority && <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                                      <span className="font-medium text-stone-800">{topic.source}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-stone-500">
                                      <span className="text-red-500 font-semibold">{topic.unhelpful_count} unhelpful</span>
                                      <span>/ {topic.total_votes} total</span>
                                      <Badge className={`text-xs ${isHighPriority ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-600'}`}>
                                        {topic.unhelpful_rate}%
                                      </Badge>
                                    </div>
                                  </div>
                                  <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full ${isHighPriority ? 'bg-amber-400' : 'bg-red-300'}`}
                                      style={{ width: `${barWidth}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                            {ragFeedback.summary.no_sources_rate > 0 && (
                              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-xs text-amber-800">
                                  <span className="font-bold">{ragFeedback.summary.no_sources_rate}%</span> of unhelpful responses had <span className="font-bold">no IRS sources returned</span> — consider adding more topic coverage.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Advisor Breakdown */}
                    <Card className="border-stone-200">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-stone-900">
                          <MessageCircle className="w-5 h-5 text-blue-500" />
                          Feedback by Advisor
                        </CardTitle>
                        <CardDescription>Which advisors need the most RAG improvement?</CardDescription>
                      </CardHeader>
                      <CardContent>
                        {Object.keys(ragFeedback.advisor_breakdown).length === 0 ? (
                          <p className="text-stone-400 text-sm text-center py-8">No feedback by advisor yet</p>
                        ) : (
                          <div className="space-y-3" data-testid="advisor-breakdown">
                            {Object.entries(ragFeedback.advisor_breakdown).map(([advisor, stats]) => {
                              // ── Commandment II: use backend satisfaction_rate if present ──
                              const rate = stats.satisfaction_rate ?? (
                                (stats.helpful || 0) + (stats.unhelpful || 0) > 0
                                  ? Math.round((stats.helpful / ((stats.helpful || 0) + (stats.unhelpful || 0))) * 100)
                                  : 0
                              );
                              return (
                                <div key={advisor} className="flex items-center gap-3">
                                  <div className="w-24 text-sm font-medium text-stone-700 capitalize truncate">{advisor}</div>
                                  <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${rate}%` }} />
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-stone-500 w-28 justify-end">
                                    <span className="text-emerald-600">{stats.helpful || 0} ✓</span>
                                    <span className="text-red-500">{stats.unhelpful || 0} ✗</span>
                                    <Badge variant="outline" className="text-xs">{rate}%</Badge>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Manage Custom Chunks */}
                  <Card className="border-stone-200">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2 text-stone-900">
                            <Layers className="w-5 h-5 text-emerald-500" />
                            Custom Knowledge Chunks
                            {customChunks.length > 0 && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs ml-1">{customChunks.length}</Badge>
                            )}
                          </CardTitle>
                          <CardDescription>Admin-authored IRS content added via the Add-to-RAG editor</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {customChunks.length === 0 ? (
                        <div className="text-center py-8">
                          <BookOpen className="w-10 h-10 mx-auto text-stone-200 mb-2" />
                          <p className="text-stone-400 text-sm">No custom chunks yet.</p>
                          <p className="text-stone-400 text-xs">Use the "Add to RAG" button on unhelpful responses below to create your first one.</p>
                        </div>
                      ) : (
                        <div className="space-y-2" data-testid="custom-chunks-list">
                          {customChunks.map((chunk, idx) => (
                            <div
                              key={chunk.id}
                              className="flex items-center gap-3 p-3 border border-stone-100 rounded-xl hover:border-emerald-200 hover:bg-emerald-50/20 transition-colors"
                              data-testid={`custom-chunk-row-${idx}`}
                            >
                              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                <BookOpen className="w-4 h-4 text-emerald-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge variant="outline" className="text-xs font-mono border-emerald-200 text-emerald-700">
                                    {chunk.pub_short}
                                  </Badge>
                                  <span className="text-sm font-medium text-stone-800 truncate">{chunk.section}</span>
                                </div>
                                <p className="text-xs text-stone-400 mt-0.5 truncate">{chunk.text?.substring(0, 80)}...</p>
                                <p className="text-xs text-stone-400">
                                  Added {chunk.created_at ? new Date(chunk.created_at).toLocaleDateString() : '—'}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteCustomChunk(chunk.id, chunk.section)}
                                disabled={deletingChunk === chunk.id}
                                className="text-red-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                                data-testid={`delete-chunk-btn-${idx}`}
                              >
                                {deletingChunk === chunk.id
                                  ? <RefreshCw className="w-4 h-4 animate-spin" />
                                  : <Trash2 className="w-4 h-4" />
                                }
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Test Retrieval Panel */}
                  <Card className="border-stone-200 border-dashed" ref={testRetrievalRef} data-testid="test-retrieval-card">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-stone-900">
                        <Search className="w-5 h-5 text-blue-500" />
                        Test RAG Retrieval
                      </CardTitle>
                      <CardDescription>
                        Type any question to see which chunks the AI would retrieve — verify your custom chunks work
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex gap-2">
                        <Input
                          placeholder="e.g. What is the 2025 mileage rate for business?"
                          value={testQuery}
                          onChange={e => setTestQuery(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && testRetrieval()}
                          className="flex-1 text-sm"
                          data-testid="test-retrieval-input"
                        />
                        <Button
                          onClick={testRetrieval}
                          disabled={testingRetrieval || !testQuery.trim()}
                          className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                          data-testid="test-retrieval-btn"
                        >
                          {testingRetrieval
                            ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Testing...</>
                            : <><Search className="w-4 h-4 mr-2" />Test Retrieval</>
                          }
                        </Button>
                      </div>

                      {testResults && (
                        <div data-testid="test-retrieval-results">
                          <p className="text-xs text-stone-500 mb-2">
                            Retrieved <strong>{testResults.total_returned}</strong> chunks for: <em>"{testResults.question}"</em>
                          </p>
                          {testResults.results.length === 0 ? (
                            <div className="text-center py-6 bg-amber-50 border border-amber-200 rounded-xl">
                              <AlertTriangle className="w-8 h-8 mx-auto text-amber-400 mb-1" />
                              <p className="text-amber-700 text-sm font-medium">No chunks retrieved above threshold.</p>
                              <p className="text-amber-600 text-xs">Consider adding a custom chunk for this topic.</p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {testResults.results.map((r, idx) => (
                                <div
                                  key={idx}
                                  className={`p-3 rounded-xl border text-sm flex items-start gap-3 ${
                                    r.is_custom
                                      ? 'bg-emerald-50 border-emerald-200'
                                      : 'bg-stone-50 border-stone-200'
                                  }`}
                                  data-testid={`retrieval-result-${idx}`}
                                >
                                  <div className="flex-shrink-0 pt-0.5">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                      r.is_custom ? 'bg-emerald-500 text-white' : 'bg-stone-300 text-stone-600'
                                    }`}>
                                      {idx + 1}
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                      <Badge variant="outline" className={`text-xs ${r.is_custom ? 'border-emerald-400 text-emerald-700' : 'border-stone-300 text-stone-600'}`}>
                                        {r.pub_short}
                                      </Badge>
                                      {r.is_custom && <Badge className="text-xs bg-emerald-100 text-emerald-700">Custom</Badge>}
                                      <span className="text-stone-700 font-medium truncate">{r.section}</span>
                                    </div>
                                    <p className="text-xs text-stone-500 line-clamp-2">{r.text_preview}...</p>
                                  </div>
                                  <div className="flex-shrink-0 text-right">
                                    <div className={`text-xs font-bold ${r.relevance > 0.5 ? 'text-emerald-600' : r.relevance > 0.35 ? 'text-amber-600' : 'text-stone-400'}`}>
                                      {Math.round(r.relevance * 100)}%
                                    </div>
                                    <div className="text-xs text-stone-400">match</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>



                  {/* Recent Unhelpful Questions */}                  <Card className="border-stone-200">                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2 text-stone-900">
                            <ThumbsDown className="w-5 h-5 text-red-500" />
                            Recent Unhelpful Responses
                          </CardTitle>
                          <CardDescription>
                            Questions users marked as unhelpful — reveals knowledge gaps to fill
                          </CardDescription>
                        </div>
                        <Button variant="outline" size="sm" onClick={fetchRagFeedback} data-testid="refresh-feedback-btn">
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Refresh
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {ragFeedback.recent_unhelpful.length === 0 ? (
                        <div className="text-center py-10">
                          <CheckCircle className="w-10 h-10 mx-auto text-emerald-400 mb-2" />
                          <p className="text-stone-500">No unhelpful votes yet — the RAG is performing well!</p>
                        </div>
                      ) : (
                        <div className="space-y-3" data-testid="recent-unhelpful-list">
                          {ragFeedback.recent_unhelpful.map((item, idx) => (
                            <div
                              key={idx}
                              className="border border-stone-200 rounded-xl p-4 hover:border-red-200 hover:bg-red-50/30 transition-colors"
                              data-testid={`unhelpful-item-${idx}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <ThumbsDown className="w-3 h-3 text-red-500" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge variant="outline" className="text-xs capitalize">{item.advisor_id}</Badge>
                                    <span className="text-xs text-stone-400">
                                      {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                                    </span>
                                  </div>
                                  <p className="text-sm font-medium text-stone-800 mb-1">"{item.user_message}"</p>
                                  {item.ai_response_preview && (
                                    <p className="text-xs text-stone-400 italic line-clamp-2">
                                      Response: {item.ai_response_preview}...
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-col gap-1.5 flex-shrink-0">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-blue-300 text-blue-700 hover:bg-blue-50 text-xs"
                                    onClick={() => testThisQuery(item.user_message)}
                                    data-testid={`test-this-query-btn-${idx}`}
                                  >
                                    <Search className="w-3 h-3 mr-1" />
                                    Test Query
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs"
                                    onClick={() => openAddToRag(item)}
                                    data-testid={`add-to-rag-btn-${idx}`}
                                  >
                                    <Zap className="w-3 h-3 mr-1" />
                                    Add to RAG
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}

            </div>
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue" data-testid="revenue-content">
            <div className="space-y-6">
              {revenueLoading && (
                <div className="flex items-center justify-center h-48">
                  <RefreshCw className="w-6 h-6 animate-spin text-emerald-500" />
                </div>
              )}
              {!revenueLoading && !revenueStats && (
                <Card><CardContent className="p-12 text-center">
                  <DollarSign className="w-12 h-12 mx-auto text-stone-200 mb-3" />
                  <p className="text-stone-500 mb-4">Click the tab to load revenue data</p>
                  <Button onClick={fetchRevenueStats} variant="outline" data-testid="load-revenue-btn">Load Revenue Stats</Button>
                </CardContent></Card>
              )}
              {!revenueLoading && revenueStats && (
                <>
                  {/* Top stat row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-emerald-50 border-emerald-200">
                      <CardContent className="p-5">
                        <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Current MRR</p>
                        <p className="text-3xl font-bold text-emerald-700" data-testid="mrr-value">
                          ${revenueStats.mrr.toFixed(0)}
                        </p>
                        <p className="text-xs text-stone-400 mt-1">Monthly recurring revenue</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-5">
                        <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Active Paid</p>
                        <p className="text-3xl font-bold text-stone-900" data-testid="active-paid-count">{revenueStats.active_paid}</p>
                        <p className="text-xs text-stone-400 mt-1">Paying subscribers</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="p-5">
                        <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Active Trials</p>
                        <p className="text-3xl font-bold text-blue-700" data-testid="active-trials-count">{revenueStats.active_trials}</p>
                        <p className="text-xs text-stone-400 mt-1">In 30-day free trial</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-5">
                        <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Total Active</p>
                        <p className="text-3xl font-bold text-stone-900" data-testid="total-active-count">{revenueStats.total_active}</p>
                        <p className="text-xs text-stone-400 mt-1">Paid + trial + grandfathered</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Revenue toggle + stats */}
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-emerald-500" />
                            Revenue
                          </CardTitle>
                          <CardDescription>Payments collected by period</CardDescription>
                        </div>
                        <div className="flex bg-stone-100 rounded-lg p-1 gap-1">
                          {[['7', '7 Days'], ['28', '28 Days']].map(([val, label]) => (
                            <button
                              key={val}
                              onClick={() => setRevenueWindow(val)}
                              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                revenueWindow === val ? 'bg-emerald-500 text-white' : 'text-stone-600 hover:bg-stone-200'
                              }`}
                              data-testid={`revenue-window-${val}`}
                            >{label}</button>
                          ))}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-6">
                        <div className="bg-stone-50 rounded-xl p-5">
                          <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">
                            Revenue — Last {revenueWindow} Days
                          </p>
                          <p className="text-4xl font-bold text-stone-900" data-testid="revenue-amount">
                            ${(revenueWindow === '7' ? revenueStats.revenue_7d : revenueStats.revenue_28d).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-stone-50 rounded-xl p-5">
                          <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">
                            New Paying Customers — Last {revenueWindow} Days
                          </p>
                          <p className="text-4xl font-bold text-stone-900" data-testid="new-customers-count">
                            {revenueWindow === '7' ? revenueStats.new_paying_7d : revenueStats.new_paying_28d}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Breakdown + Founding slots */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">Customer Breakdown</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {[
                          { label: 'Active Paid',       value: revenueStats.active_paid,    color: 'bg-emerald-500' },
                          { label: 'Active Trials',     value: revenueStats.active_trials,  color: 'bg-blue-500' },
                          { label: 'Grandfathered',     value: revenueStats.grandfathered,  color: 'bg-stone-400' },
                          { label: 'Locked Out',        value: revenueStats.locked_out,     color: 'bg-amber-500' },
                          { label: 'Data Deleted',      value: revenueStats.data_deleted,   color: 'bg-red-400' },
                        ].map(({ label, value, color }) => (
                          <div key={label} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
                              <span className="text-sm text-stone-700">{label}</span>
                            </div>
                            <Badge variant="outline">{value}</Badge>
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    <Card className="bg-amber-50 border-amber-200">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-base">
                          <Crown className="w-4 h-4 text-amber-500" />
                          Founding Member Slots
                        </CardTitle>
                        <CardDescription>Yearly plan available until 100 paid members</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-end gap-2 mb-3">
                          <p className="text-4xl font-bold text-amber-700" data-testid="founding-slots-remaining">
                            {revenueStats.founding_slots_remaining}
                          </p>
                          <p className="text-stone-500 mb-1">/ 100 slots remaining</p>
                        </div>
                        <div className="h-2 bg-amber-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-amber-500 rounded-full transition-all"
                            style={{ width: `${Math.min(100, revenueStats.founding_slots_used)}%` }}
                          />
                        </div>
                        <p className="text-xs text-amber-700 mt-2">
                          {revenueStats.founding_slots_used} used · {revenueStats.founding_slots_remaining} remaining
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="flex justify-end">
                    <Button variant="outline" size="sm" onClick={fetchRevenueStats} data-testid="refresh-revenue-btn">
                      <RefreshCw className="w-4 h-4 mr-2" />Refresh
                    </Button>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* ── Feature Flags Tab ─────────────────────────────────────────── */}
          <TabsContent value="flags" data-testid="flags-tab-content">
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-lg">Feature Flags</h3>
                    <p className="text-sm text-gray-500">
                      Enable / disable WS-4 features and control rollout percentage.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm"
                      onClick={() => { setShowAudit(!showAudit); if (!showAudit) fetchFlagAuditLog(); }}
                      data-testid="toggle-audit-btn">
                      <Flag className="w-4 h-4 mr-1" />Audit Log
                    </Button>
                    <Button variant="outline" size="sm" onClick={fetchFlags}
                      data-testid="refresh-flags-btn">
                      <RefreshCw className="w-4 h-4 mr-1" />Refresh
                    </Button>
                  </div>
                </div>

                {/* Audit log panel */}
                {showAudit && (
                  <div className="border border-gray-200 rounded-xl overflow-hidden"
                    data-testid="flag-audit-panel">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700">Audit Trail</span>
                      <span className="text-xs text-gray-400">{flagAuditLog.length} events</span>
                    </div>
                    {flagAuditLog.length === 0 ? (
                      <p className="text-sm text-gray-400 px-4 py-3">No audit events yet.</p>
                    ) : (
                      <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                        {flagAuditLog.map((evt, i) => (
                          <div key={i} className="px-4 py-2.5 flex items-start gap-3 text-xs">
                            <div className="flex-1 min-w-0">
                              <span className="font-mono font-semibold text-gray-800">{evt.flag_name}</span>
                              <span className="mx-2 text-gray-400">·</span>
                              <span className={`font-semibold ${
                                evt.action === 'enabled' ? 'text-green-600' :
                                evt.action === 'disabled' ? 'text-red-600' : 'text-blue-600'
                              }`}>{evt.action?.replace(/_/g, ' ')}</span>
                              {evt.detail && <span className="text-gray-500 ml-2">{evt.detail}</span>}
                            </div>
                            <div className="flex-shrink-0 text-right text-gray-400 space-y-0.5">
                              <p>{evt.actor_email}</p>
                              <p className="font-mono text-gray-300">{new Date(evt.created_at).toLocaleString()}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Search */}
                <input
                  value={flagSearch}
                  onChange={e => setFlagSearch(e.target.value)}
                  placeholder="Search flags…"
                  data-testid="flag-search-input"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />

                {flagsError && (
                  <p className="text-sm text-red-600">{flagsError}</p>
                )}

                {flagsLoading ? (
                  <p className="text-sm text-gray-400">Loading flags…</p>
                ) : flags.length === 0 ? (
                  <p className="text-sm text-gray-400">No feature flags found.</p>
                ) : (
                  <div className="space-y-3">
                    {flags
                      .filter(f => !flagSearch || f.name.toLowerCase().includes(flagSearch.toLowerCase()) ||
                                   (f.description || '').toLowerCase().includes(flagSearch.toLowerCase()))
                      .map(flag => (
                        <div key={flag.name}
                          className="border border-gray-200 rounded-xl p-4"
                          data-testid={`flag-row-${flag.name}`}>

                          {/* Header row */}
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-sm font-semibold text-gray-900">
                                  {flag.name}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                  flag.enabled
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-gray-100 text-gray-500'
                                }`}>
                                  {flag.enabled ? 'Enabled' : 'Disabled'}
                                </span>
                              </div>
                              {flag.description && (
                                <p className="text-xs text-gray-500 mt-0.5">{flag.description}</p>
                              )}
                            </div>

                            {/* Enable / Disable toggle */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {flag.enabled
                                ? <ToggleRight className="w-4 h-4 text-green-600" />
                                : <ToggleLeft className="w-4 h-4 text-gray-400" />
                              }
                              <Switch
                                checked={!!flag.enabled}
                                onCheckedChange={v => setFlagEnabled(flag.name, v)}
                                data-testid={`flag-toggle-${flag.name}`}
                              />
                            </div>
                          </div>

                          {/* Rollout percentage slider */}
                          <div className="mt-3 space-y-1">
                            <div className="flex items-center justify-between">
                              <label className="text-xs text-gray-500 font-medium">
                                Rollout
                              </label>
                              <span className="text-xs font-semibold text-gray-700"
                                data-testid={`flag-rollout-pct-${flag.name}`}>
                                {flag.rollout_percentage ?? 0}%
                              </span>
                            </div>
                            <Slider
                              min={0} max={100} step={5}
                              value={[flag.rollout_percentage ?? 0]}
                              onValueChange={([v]) =>
                                // Update display only — no API call on every drag tick
                                setFlags(prev => prev.map(f =>
                                  f.name === flag.name ? { ...f, rollout_percentage: v } : f
                                ))
                              }
                              onValueCommit={([v]) =>
                                // API call only fires when user releases the slider
                                setFlagRollout(flag.name, v)
                              }
                              data-testid={`flag-slider-${flag.name}`}
                              className="w-full"
                              disabled={!flag.enabled}
                            />
                            <p className="text-xs text-gray-400">
                              {flag.rollout_percentage === 0
                                ? 'Targeted users only (0% general rollout)'
                                : flag.rollout_percentage === 100
                                  ? '100% of eligible users'
                                  : `${flag.rollout_percentage}% of eligible users randomly`}
                            </p>
                          </div>

                          {/* Quick user grant */}
                          <details className="mt-3">
                            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-700">
                              Grant to specific user
                            </summary>
                            <div className="mt-2 flex gap-2">
                              <input
                                id={`user-grant-${flag.name}`}
                                placeholder="user_id or email"
                                className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs"
                              />
                              <button
                                onClick={() => {
                                  const el = document.getElementById(`user-grant-${flag.name}`);
                                  if (el?.value) setFlagForUser(flag.name, el.value, true);
                                }}
                                className="bg-gray-900 text-white text-xs px-3 py-1 rounded font-semibold"
                              >
                                Grant
                              </button>
                            </div>
                          </details>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>

        {/* User Detail Dialog */}
        <Dialog open={userDetailOpen} onOpenChange={setUserDetailOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                User Tool Usage Detail
              </DialogTitle>
              <DialogDescription>
                Detailed tool usage and financial metrics for this user
              </DialogDescription>
            </DialogHeader>
            {userToolDetail && (
              <div className="space-y-4">
                <div className="bg-slate-100 p-4 rounded-lg">
                  <p className="font-semibold text-gray-900">{userToolDetail.user?.name || 'Unnamed User'}</p>
                  <p className="text-sm text-gray-600">{userToolDetail.user?.email}</p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <Card className="bg-emerald-50 border border-emerald-200">
                    <CardContent className="pt-4 pb-4 text-center">
                      <p className="text-2xl font-bold text-emerald-800">{formatCurrency(userToolDetail.financials?.total_income)}</p>
                      <p className="text-xs text-emerald-700 font-medium">Total Income</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50 border border-red-200">
                    <CardContent className="pt-4 pb-4 text-center">
                      <p className="text-2xl font-bold text-red-800">{formatCurrency(userToolDetail.financials?.total_expenses)}</p>
                      <p className="text-xs text-red-700 font-medium">Total Expenses</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50 border border-blue-200">
                    <CardContent className="pt-4 pb-4 text-center">
                      <p className="text-2xl font-bold text-blue-800">{formatCurrency(userToolDetail.financials?.net_income)}</p>
                      <p className="text-xs text-blue-700 font-medium">Net Income</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold mb-3 text-gray-900">Transactions</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-gray-600">CSV Imports:</span><span className="text-gray-900 font-medium">{userToolDetail.tools?.transactions?.csv_imports}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Manual Entry:</span><span className="text-gray-900 font-medium">{userToolDetail.tools?.transactions?.manual_entry}</span></div>
                      <div className="flex justify-between font-semibold border-t border-gray-200 pt-2 mt-2"><span className="text-gray-900">Total:</span><span className="text-gray-900">{userToolDetail.tools?.transactions?.total}</span></div>
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold mb-3 text-gray-900">Receipts</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-gray-600">OCR Scanned:</span><span className="text-gray-900 font-medium">{userToolDetail.tools?.receipts?.ocr_scanned}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Manual Upload:</span><span className="text-gray-900 font-medium">{userToolDetail.tools?.receipts?.manual_upload}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Linked:</span><span className="text-emerald-700 font-semibold">{userToolDetail.tools?.receipts?.linked}</span></div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 text-gray-900">Other Tools</h4>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div className="bg-gray-50 p-3 rounded-lg text-center border border-gray-200">
                      <p className="font-bold text-gray-900 text-lg">{userToolDetail.tools?.ai_chat?.total_messages}</p>
                      <p className="text-xs text-gray-600 font-medium">AI Chats</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg text-center border border-gray-200">
                      <p className="font-bold text-gray-900 text-lg">{userToolDetail.tools?.mileage_entries}</p>
                      <p className="text-xs text-gray-600 font-medium">Mileage</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg text-center border border-gray-200">
                      <p className="font-bold text-gray-900 text-lg">{userToolDetail.tools?.contractors}</p>
                      <p className="text-xs text-gray-600 font-medium">Contractors</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg text-center border border-gray-200">
                      <p className="font-bold text-gray-900 text-lg">{userToolDetail.tools?.audit_reports}</p>
                      <p className="text-xs text-gray-600 font-medium">Audit Reports</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg text-center border border-gray-200">
                      <p className="font-bold text-gray-900 text-lg">{userToolDetail.tools?.tax_packages}</p>
                      <p className="text-xs text-gray-600 font-medium">Tax Packages</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg text-center border border-gray-200">
                      <p className="font-bold text-gray-900 text-lg">{userToolDetail.tools?.reminders}</p>
                      <p className="text-xs text-gray-600 font-medium">Reminders</p>
                    </div>
                  </div>
                </div>

                {userToolDetail.tools?.ai_chat?.by_advisor && Object.keys(userToolDetail.tools.ai_chat.by_advisor).length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold mb-3 text-gray-900">AI Chat by Advisor</h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(userToolDetail.tools.ai_chat.by_advisor).map(([advisor, count]) => (
                        <Badge key={advisor} variant="outline" className="text-gray-700 border-gray-300">
                          {advisor}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add to RAG Dialog */}
        <Dialog open={!!ragChunkModal} onOpenChange={(open) => !open && setRagChunkModal(null)}>
          <DialogContent className="max-w-2xl" data-testid="add-to-rag-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-emerald-500" />
                Add to RAG Knowledge Base
              </DialogTitle>
              <DialogDescription>
                Write the IRS content that answers this question. It will be embedded and added to the live knowledge base immediately.
              </DialogDescription>
            </DialogHeader>

            {ragChunkModal && (
              <div className="space-y-4">
                {/* Source question context */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-600 font-medium mb-1">Gap identified from unhelpful response:</p>
                  <p className="text-sm text-stone-800 italic">"{ragChunkModal.user_message}"</p>
                </div>

                {/* Form fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-stone-700">Publication Short Name *</Label>
                    <Input
                      placeholder="e.g. Pub 535"
                      value={ragChunkForm.pub_short}
                      onChange={e => setRagChunkForm(f => ({ ...f, pub_short: e.target.value }))}
                      className="text-sm"
                      data-testid="rag-pub-short"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-stone-700">Full Publication Name</Label>
                    <Input
                      placeholder="e.g. IRS Publication 535"
                      value={ragChunkForm.publication}
                      onChange={e => setRagChunkForm(f => ({ ...f, publication: e.target.value }))}
                      className="text-sm"
                      data-testid="rag-publication"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-stone-700">Section Title *</Label>
                  <Input
                    placeholder="e.g. Business Vehicle Expenses — Lease vs. Buy"
                    value={ragChunkForm.section}
                    onChange={e => setRagChunkForm(f => ({ ...f, section: e.target.value }))}
                    className="text-sm"
                    data-testid="rag-section"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-stone-700">IRS Source URL (optional)</Label>
                  <Input
                    placeholder="https://www.irs.gov/publications/p535"
                    value={ragChunkForm.url}
                    onChange={e => setRagChunkForm(f => ({ ...f, url: e.target.value }))}
                    className="text-sm"
                    autoComplete="off"
                    data-testid="rag-url"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-stone-700">IRS Content *</Label>
                  <textarea
                    rows={8}
                    placeholder="Write the IRS publication content that directly answers the user's question. Include rule names, specific numbers, thresholds, and actionable guidance."
                    value={ragChunkForm.text}
                    onChange={e => setRagChunkForm(f => ({ ...f, text: e.target.value }))}
                    className="w-full text-sm border border-stone-200 rounded-md px-3 py-2 resize-y focus:outline-none focus:ring-2 focus:ring-emerald-500 placeholder:text-stone-400"
                    data-testid="rag-content"
                  />
                  <p className="text-xs text-stone-400">{ragChunkForm.text.length} chars — aim for 200–600 words for best retrieval quality</p>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setRagChunkModal(null)}>Cancel</Button>
              <Button
                onClick={saveRagChunk}
                disabled={savingChunk || !ragChunkForm.pub_short || !ragChunkForm.section || !ragChunkForm.text}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="save-rag-chunk-btn"
              >
                {savingChunk ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Adding...</> : <><Zap className="w-4 h-4 mr-2" />Add to Knowledge Base</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
